import { useState, type JSX } from 'react';
import type { BoardApi } from './useBoard.ts';
import type { FormationsApi } from './useFormations.ts';

interface FormationControlsProps {
  readonly api: BoardApi;
  readonly formations: FormationsApi;
}

/**
 * Recalling a formation — the pack's own or one the coach saved — from one
 * menu, saving the current phase as a formation, and deleting saved ones.
 */
export function FormationControls({ api, formations }: FormationControlsProps): JSX.Element {
  const [naming, setNaming] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const replaces = naming !== null ? formations.named(naming) : undefined;

  const finishSaving = async () => {
    if (naming === null || !naming.trim()) return;
    if (await formations.save(naming)) setNaming(null);
  };

  return (
    <div className="formation-controls" data-tutorial="formations">
      <label className="field">
        <span>Formation</span>
        <select
          aria-label="Formation"
          onChange={(event) => {
            const [kind, id] = event.target.value.split(':');
            if (kind === 'builtin' && id) api.resetTo(id);
            if (kind === 'saved' && id) formations.recall(id);
            event.target.selectedIndex = 0;
          }}
          defaultValue=""
        >
          <option value="">Reset to…</option>
          <optgroup label="Built-in">
            {api.pack.formations.map((formation) => (
              <option key={formation.id} value={`builtin:${formation.id}`}>
                {formation.name}
              </option>
            ))}
          </optgroup>
          {formations.saved.length > 0 && (
            <optgroup label="Saved">
              {formations.saved.map((formation) => (
                <option key={formation.id} value={`saved:${formation.id}`}>
                  {formation.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>

      {naming === null ? (
        <button type="button" className="btn btn--ghost" onClick={() => setNaming('')}>
          Save as formation…
        </button>
      ) : (
        <form
          className="formation-save"
          onSubmit={(event) => {
            event.preventDefault();
            void finishSaving();
          }}
        >
          <input
            type="text"
            aria-label="Formation name"
            placeholder="Name this formation"
            maxLength={60}
            value={naming}
            autoFocus
            onChange={(event) => setNaming(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setNaming(null);
            }}
          />
          <div className="toolbar__row">
            <button type="submit" className="btn" disabled={!naming.trim()}>
              {replaces ? 'Replace' : 'Save'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setNaming(null)}>
              Cancel
            </button>
          </div>
          <p className="toolbar__hint">
            {replaces
              ? `Replaces your saved “${replaces.name}”.`
              : 'Saves this phase: every player’s spot, number and position.'}
          </p>
        </form>
      )}

      {formations.saved.length > 0 && (
        <details className="saved-formations">
          <summary>Saved formations ({formations.saved.length})</summary>
          <ul>
            {formations.saved.map((formation) => (
              <li key={formation.id}>
                <span className="saved-formations__name">{formation.name}</span>
                {confirming === formation.id ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => {
                        setConfirming(null);
                        void formations.remove(formation.id);
                      }}
                    >
                      Delete
                    </button>
                    <button type="button" className="btn btn--ghost" onClick={() => setConfirming(null)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    aria-label={`Delete ${formation.name}`}
                    title={`Delete ${formation.name}`}
                    onClick={() => setConfirming(formation.id)}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {formations.unreadable > 0 && (
        <p className="toolbar__hint">
          {formations.unreadable} saved with a newer version of the app {formations.unreadable === 1 ? 'is' : 'are'} kept
          but not shown here.
        </p>
      )}
      {formations.error && <p className="toolbar__hint toolbar__hint--error">{formations.error}</p>}
    </div>
  );
}
